/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  AppstoreAddOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Dropdown, MenuProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import DraggableQuestion from './DraggableQuestion'
import DragHandle from './DragHandle'
import MoreIcon from './MoreIcon'
import ChevronIcon from './ChevronIcon'
import SaveIcon from './SaveIcon'
import CancelIcon from './CancelIcon'
import RichTextEditor from './RichTextEditor'
import OptionEditor from './OptionEditor'
import NumberRangeInputs from './NumberRangeInputs'
import QuestionTypeSelector from './QuestionTypeSelector'
import {
  Question,
  LegacyQuestion,
  Option,
  QuestionFormDraft,
  isChoiceQuestion,
  generateId,
  convertLegacyToNewQuestion,
  convertNewToLegacyQuestion,
} from './types'
import {
  isQuestionFormValid,
  getDisabledButtonStyles,
} from 'src/utils/questionFormValidation'
import { isAnyPresentQuestionEditing } from './questionEditingState'
import RichTextRenderer from 'src/components/RichTextRenderer'
import { SelectInput } from 'src/components/ts-components/SelectInput'

const usaHockeyValidationOptions = [
  { value: 'none', label: 'No Validation' },
  { value: 'partial', label: 'Partial Validation (Last Name + Birth Date)' },
  {
    value: 'full',
    label: 'Full Validation (First/Last Name + Birth Date)',
  },
]

const dropdownOverlayStyle = { zIndex: 10008 }

const dropdownRender = (menu: React.ReactElement | undefined) => (
  <div
    css={css`
      background: ${colors.SECONDARY_400};
      border-radius: 8px;
      border: 1px solid ${colors.SECONDARY_200};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 4px;
      min-width: 160px;
    `}
  >
    {menu}
  </div>
)

interface DraggableSectionProps {
  id: string
  title: string
  subtitle: string
  isEnabled?: boolean
  isReserved?: boolean
  isContent?: boolean
  content?: string
  questions?: LegacyQuestion[]
  onToggle?: (id: string, enabled: boolean) => void
  onEdit: (
    id: string,
    title: string,
    subtitle: string,
    content?: string
  ) => void
  onDelete: (id: string) => void
  onNewQuestion: (id: string) => void
  onAddQuestion?: (sectionId: string, question: LegacyQuestion) => void
  onInsertSection?: (afterSectionId: string) => void
  onInsertContent?: (afterSectionId: string) => void
  onUpdateQuestions?: (sectionId: string, questions: LegacyQuestion[]) => void
  onQuestionToggle?: (
    sectionId: string,
    questionId: string,
    enabled: boolean
  ) => void
  onQuestionDelete?: (sectionId: string, questionId: string) => void
  onQuestionUpdate?: (
    sectionId: string,
    questionId: string,
    updatedQuestion: Partial<LegacyQuestion>
  ) => void
  fieldErrors?: any
  customError?: any
}

interface DraggableSectionRef {
  hasPendingChanges: () => boolean
}

// Helper function to check if rich text content is truly empty
const isRichTextEmpty = (content: string): boolean => {
  if (!content || content.trim() === '') {
    return true
  }
  // Strip HTML tags and check if remaining text is empty
  const strippedText = content.replace(/<[^>]*>/g, '').trim()
  return strippedText.length === 0
}

const DraggableSection = forwardRef<DraggableSectionRef, DraggableSectionProps>(
  (
    {
      id,
      title,
      subtitle,
      isEnabled: isEnabledProp = true,
      isReserved = false,
      isContent = false,
      content = '',
      questions = [],
      onToggle,
      onEdit,
      onDelete,
      onNewQuestion,
      onAddQuestion,
      onInsertSection,
      onInsertContent,
      onUpdateQuestions,
      onQuestionToggle,
      onQuestionDelete,
      onQuestionUpdate,
      fieldErrors = {},
      customError = {},
    },
    ref
  ) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editTitle, setEditTitle] = useState(title)
    const [editSubtitle, setEditSubtitle] = useState(subtitle)
    const [editContent, setEditContent] = useState(content)
    const [isAddingQuestion, setIsAddingQuestion] = useState(false)
    const [questionForm, setQuestionForm] = useState<QuestionFormDraft>({
      label: '',
      type: 'text' as Question['type'],
      placeholder: '',
      description: '',
      mandatory: false,
      options: [] as Option[],
      min: '',
      max: '',
      max_char: '',
      validate_schedule_dates: true,
      usa_hockey_validation_type: 'none',
    })

    const [localQuestions, setLocalQuestions] = useState<Question[]>(
      questions.map(convertLegacyToNewQuestion)
    )

    const [isCollapsed, setIsCollapsed] = useState(false)
    const [questionEditingMap, setQuestionEditingMap] = useState<
      Record<string, boolean>
    >({})

    // Ref for Add Question form to enable auto-scroll
    const addQuestionFormRef = useRef<HTMLDivElement | null>(null)

    // Configure DnD sensors for questions
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id })

    // Derive local enabled flag exactly as DraggableQuestion does
    const isEnabled = isEnabledProp !== false

    // Update local questions when props change - convert legacy to new format
    useEffect(() => {
      const convertedQuestions = questions.map(convertLegacyToNewQuestion)
      setLocalQuestions(convertedQuestions)
    }, [questions])

    // Auto-scroll to Add Question form when it becomes visible
    useEffect(() => {
      if (isAddingQuestion) {
        console.log('🔄 Auto-scrolling to Add Question form')
        addQuestionFormRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        // Focus the first input field for better UX
        const input = addQuestionFormRef.current?.querySelector('input')
        if (input instanceof HTMLInputElement) {
          // Small delay to ensure scroll animation starts first
          setTimeout(() => input.focus(), 100)
        }
      }
    }, [isAddingQuestion])

    // Compute sorted questions with useMemo to avoid infinite re-renders
    const sortedLocalQuestions = useMemo(() => {
      return [...localQuestions].sort((a, b) => a.order - b.order)
    }, [localQuestions])

    // Validate the Add Question form
    const isAddFormValid = useMemo(() => {
      return isQuestionFormValid(questionForm)
    }, [questionForm])

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0 : 1, // Hide the original when dragging (overlay shows instead)
    }

    const handleEditClick = () => {
      if (isReserved) {
        return
      } // Don't allow editing reserved sections
      setEditTitle(title)
      setEditSubtitle(subtitle)
      setEditContent(content)
      setIsEditing(true)
    }

    const handleSave = () => {
      if (isContent) {
        // For content sections, require both title and content
        if (editTitle.trim() && !isRichTextEmpty(editContent)) {
          onEdit(id, editTitle, editSubtitle, editContent)
          setIsEditing(false)
        }
      } else {
        // For regular sections, require title
        if (editTitle.trim()) {
          onNewQuestion(id)
          onEdit(id, editTitle, editSubtitle)
          setIsEditing(false)
        }
      }
    }

    const handleCancel = () => {
      setEditTitle(title)
      setEditSubtitle(subtitle)
      setEditContent(content)
      setIsEditing(false)
    }

    const initializeQuestionForm = (type: Question['type'] = 'text') => {
      // Special handling for USA Hockey type
      if (type === 'hockey_usa') {
        return {
          label: 'USA Hockey Number', // Fixed title
          type,
          placeholder: '',
          description: '',
          mandatory: true, // Always required for USA Hockey
          options: [],
          min: '',
          max: '',
          max_char: '',
          validate_schedule_dates: true,
          usa_hockey_validation_type: 'none' as const,
        }
      }

      return {
        label: '',
        type,
        placeholder: '',
        description: '',
        mandatory: false,
        options: isChoiceQuestion({ type } as Question)
          ? [{ id: generateId(), label: '' }]
          : [],
        min: '',
        max: '',
        max_char: '',
        validate_schedule_dates: true, // Default to true for all types
        usa_hockey_validation_type: 'none' as const,
      }
    }

    const handleQuestionTypeChange = (type: string) => {
      const newType = type as Question['type']

      // Special handling for USA Hockey - reinitialize the entire form
      if (newType === 'hockey_usa') {
        setQuestionForm(initializeQuestionForm('hockey_usa'))
        return
      }

      setQuestionForm((prev) => ({
        ...prev,
        type: newType,
        options: isChoiceQuestion({ type: newType } as Question)
          ? prev.options.length > 0
            ? prev.options
            : [{ id: generateId(), label: '' }]
          : [],
      }))
    }

    const handleNewQuestionClick = () => {
      setIsAddingQuestion(true)
      setQuestionForm(initializeQuestionForm())
      // Expand the section if it's collapsed when adding a new question
      if (isCollapsed) {
        setIsCollapsed(false)
      }
    }

    const handleQuestionSave = () => {
      if (questionForm.label.trim() && onAddQuestion) {
        let newQuestion: Question

        if (isChoiceQuestion({ type: questionForm.type } as Question)) {
          // Validate that at least one option has a label
          const validOptions = questionForm.options.filter((opt) =>
            opt.label.trim()
          )
          if (validOptions.length === 0) {
            return // Don't save if no valid options
          }

          newQuestion = {
            field_id: `field_${Date.now()}`,
            label: questionForm.label,
            type: questionForm.type as 'dropdown' | 'checkbox',
            subtype: questionForm.type,
            mandatory: questionForm.mandatory,
            placeholder: questionForm.placeholder,
            description: questionForm.description,
            order: localQuestions.length + 1,
            enabled: true,
            options: validOptions,
          }
        } else if (questionForm.type === 'number') {
          newQuestion = {
            field_id: `field_${Date.now()}`,
            label: questionForm.label,
            type: 'number',
            subtype: questionForm.type,
            mandatory: questionForm.mandatory,
            placeholder: questionForm.placeholder,
            description: questionForm.description,
            order: localQuestions.length + 1,
            enabled: true,
            min: questionForm.min ? Number(questionForm.min) : undefined,
            max: questionForm.max ? Number(questionForm.max) : undefined,
          }
        } else if (
          ['text', 'textarea', 'email', 'url'].includes(questionForm.type)
        ) {
          newQuestion = {
            field_id: `field_${Date.now()}`,
            label: questionForm.label,
            type: questionForm.type as 'text' | 'textarea' | 'email' | 'url',
            subtype: questionForm.type,
            mandatory: questionForm.mandatory,
            placeholder: questionForm.placeholder,
            description: questionForm.description,
            order: localQuestions.length + 1,
            enabled: true,
            max_char: questionForm.max_char
              ? Number(questionForm.max_char)
              : undefined,
          }
        } else if (questionForm.type === 'hockey_usa') {
          // USA Hockey field - always required with fixed title
          newQuestion = {
            field_id: `field_${Date.now()}`,
            label: 'USA Hockey Number', // Fixed title
            type: 'hockey_usa',
            subtype: questionForm.type,
            mandatory: true, // Always required
            placeholder: questionForm.placeholder,
            description: questionForm.description,
            order: localQuestions.length + 1,
            enabled: true,
            validate_schedule_dates: questionForm.validate_schedule_dates,
            usa_hockey_validation_type: questionForm.usa_hockey_validation_type,
          }
        } else {
          // For other types like country, date, birthdate, phone, relationship,
          // teamOfficialRole that don't need special parameters authored here
          newQuestion = {
            field_id: `field_${Date.now()}`,
            label: questionForm.label,
            type: questionForm.type as
              | 'country'
              | 'date'
              | 'birthdate'
              | 'phone'
              | 'player_position'
              | 'relationship'
              | 'teamOfficialRole',
            subtype: questionForm.type,
            mandatory: questionForm.mandatory,
            placeholder: questionForm.placeholder,
            description: questionForm.description,
            order: localQuestions.length + 1,
            enabled: true,
          }
        }

        onAddQuestion(id, convertNewToLegacyQuestion(newQuestion))
        setIsAddingQuestion(false)
        setQuestionForm(initializeQuestionForm())
      }
    }

    // Handle question drag end event
    const handleQuestionDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
          setLocalQuestions((lQuestions) => {
            const oldIndex = lQuestions.findIndex(
              (question) => question.field_id === active.id
            )
            const newIndex = lQuestions.findIndex(
              (question) => question.field_id === over.id
            )
            const reorderedQuestions = arrayMove(lQuestions, oldIndex, newIndex)

            // Update order values based on new positions
            const updatedQuestions = reorderedQuestions.map(
              (lQuestion, index) => ({
                ...lQuestion,
                order: index + 1,
              })
            )

            // Update parent component
            if (onUpdateQuestions) {
              onUpdateQuestions(
                id,
                updatedQuestions.map(convertNewToLegacyQuestion)
              )
            }

            return updatedQuestions
          })
        }
      },
      [id, onUpdateQuestions]
    )

    // Handle question toggle with optimistic update
    const handleQuestionToggle = useCallback(
      (questionId: string, enabled: boolean) => {
        // Optimistic update: update local state immediately
        setLocalQuestions((prevQuestions) =>
          prevQuestions.map((question) =>
            question.field_id === questionId
              ? { ...question, enabled }
              : question
          )
        )

        // Update parent component (API call happens here)
        if (onQuestionToggle) {
          onQuestionToggle(id, questionId, enabled)
        }
      },
      [id, onQuestionToggle]
    )

    // Handle question delete
    const handleQuestionDelete = useCallback(
      (questionId: string) => {
        if (onQuestionDelete) {
          onQuestionDelete(id, questionId)
        }
      },
      [id, onQuestionDelete]
    )

    // Handle question update
    const handleQuestionUpdate = useCallback(
      (questionId: string, updatedQuestion: Partial<Question>) => {
        if (onQuestionUpdate) {
          // Convert the partial new question to partial legacy question
          const legacyUpdate: Partial<LegacyQuestion> = {
            field_id: updatedQuestion.field_id,
            label: updatedQuestion.label,
            displayLabel: updatedQuestion.displayLabel,
            type: updatedQuestion.type,
            subtype: updatedQuestion.subtype,
            mandatory: updatedQuestion.mandatory,
            placeholder: updatedQuestion.placeholder,
            description: updatedQuestion.description,
            order: updatedQuestion.order,
            enabled: updatedQuestion.enabled,
          }

          // Include type-specific properties
          if (updatedQuestion.type === 'hockey_usa') {
            legacyUpdate.validate_schedule_dates =
              updatedQuestion.validate_schedule_dates
            legacyUpdate.usa_hockey_validation_type = (
              updatedQuestion as any
            ).usa_hockey_validation_type
          }
          if (
            updatedQuestion.type &&
            ['dropdown', 'checkbox'].includes(updatedQuestion.type)
          ) {
            legacyUpdate.options = (updatedQuestion as any).options
          } else if (updatedQuestion.type === 'number') {
            legacyUpdate.min = (updatedQuestion as any).min
            legacyUpdate.max = (updatedQuestion as any).max
          } else if (
            updatedQuestion.type &&
            ['text', 'textarea', 'email', 'url'].includes(updatedQuestion.type)
          ) {
            legacyUpdate.max_char = (updatedQuestion as any).max_char
          }

          onQuestionUpdate(id, questionId, legacyUpdate)
        }
      },
      [id, onQuestionUpdate]
    )

    const handleQuestionCancel = () => {
      setIsAddingQuestion(false)
      setQuestionForm(initializeQuestionForm())
    }

    // Handle question editing state changes
    const handleQuestionEditStateChange = useCallback(
      (questionId: string, lIsEditing: boolean) => {
        setQuestionEditingMap((prev) => ({
          ...prev,
          [questionId]: lIsEditing,
        }))
      },
      []
    )

    // Only consider edit state of questions still present in this section. A
    // question deleted while in edit mode leaves a stale `true` in
    // questionEditingMap (its DraggableQuestion unmounts without reporting
    // back); scoping to present questions stops that from being read as a
    // pending change and falsely triggering the "Unsaved Changes" warning.
    const isAnyQuestionEditing = isAnyPresentQuestionEditing(
      questionEditingMap,
      localQuestions.map((question) => question.field_id)
    )

    // Expose hasPendingChanges method to parent
    useImperativeHandle(
      ref,
      () => ({
        hasPendingChanges: () =>
          isEditing || isAddingQuestion || isAnyQuestionEditing,
      }),
      [isEditing, isAddingQuestion, isAnyQuestionEditing]
    )

    const menuItems: MenuProps['items'] = [
      // Only show "Edit Section/Content" if not reserved
      ...(isReserved
        ? []
        : [
            {
              key: 'edit',
              label: (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 0;
                  `}
                >
                  <EditOutlined
                    css={css`
                      color: ${colors.PRIMARY};
                      font-size: 14px;
                    `}
                  />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    {isContent ? 'Edit Content' : 'Edit Section'}
                  </Text>
                </div>
              ),
              onClick: handleEditClick,
            },
          ]),
      // Enable/Disable toggle - only for non-reserved sections. Clicking the row
      // toggles it directly (no inline switch).
      ...(isReserved
        ? []
        : [
            {
              key: 'toggle-enabled',
              label: (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 0;
                  `}
                >
                  {isEnabled ? (
                    <StopOutlined
                      css={css`
                        color: ${colors.PRIMARY};
                        font-size: 14px;
                      `}
                    />
                  ) : (
                    <CheckCircleOutlined
                      css={css`
                        color: ${colors.PRIMARY};
                        font-size: 14px;
                      `}
                    />
                  )}
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    {isEnabled
                      ? isContent
                        ? 'Disable Content'
                        : 'Disable Section'
                      : isContent
                      ? 'Enable Content'
                      : 'Enable Section'}
                  </Text>
                </div>
              ),
              onClick: ({ domEvent }) => {
                domEvent.stopPropagation()
                onToggle?.(id, !isEnabled)
              },
            },
          ]),
      // Only show "Delete Section/Content" if not reserved
      ...(isReserved
        ? []
        : [
            {
              key: 'delete',
              label: (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 0;
                  `}
                >
                  <DeleteOutlined
                    css={css`
                      color: ${colors.ERROR_300};
                      font-size: 14px;
                    `}
                  />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    {isContent ? 'Delete Content' : 'Delete Section'}
                  </Text>
                </div>
              ),
              onClick: () => onDelete(id),
            },
          ]),
    ]

    if (isEditing) {
      return (
        <div
          ref={setNodeRef}
          style={style}
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            padding: 8px;
            background: ${colors.SECONDARY_400};
            border-radius: 16px;
            border: 1px solid ${colors.PRIMARY};
          `}
        >
          <div
            css={css`
              flex: 1 1 0;
              align-self: stretch;
              justify-content: flex-start;
              align-items: center;
              gap: 8px;
              display: flex;
            `}
          >
            <div
              css={css`
                flex: 1 1 0;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 8px;
                display: inline-flex;
              `}
            >
              {isContent ? (
                // Title and content fields for content sections
                <>
                  <div
                    css={css`
                      align-self: stretch;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <TextInput
                      name={`edit-content-title-${id}`}
                      value={editTitle}
                      setValue={setEditTitle}
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Section Name *'
                      placeholder='Enter section name'
                    />
                  </div>
                  <RichTextEditor
                    value={editContent}
                    onChange={setEditContent}
                    label='Content *'
                    placeholder='Enter rich text content...'
                  />
                </>
              ) : (
                // Regular title/subtitle inputs for normal sections
                <>
                  <div
                    css={css`
                      align-self: stretch;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 16px;
                      display: inline-flex;
                    `}
                  >
                    <div
                      css={css`
                        flex: 1 1 0;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: flex-start;
                        gap: 8px;
                        display: inline-flex;
                      `}
                    >
                      <TextInput
                        name={`edit-title-${id}`}
                        value={editTitle}
                        setValue={setEditTitle}
                        fieldErrors={fieldErrors}
                        customError={customError}
                        label='Title *'
                        placeholder='Enter section title'
                      />
                    </div>
                  </div>

                  <div
                    css={css`
                      align-self: stretch;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <TextInput
                      name={`edit-subtitle-${id}`}
                      value={editSubtitle}
                      setValue={setEditSubtitle}
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Subtitle'
                      placeholder='Enter section subtitle (optional)'
                    />
                  </div>
                </>
              )}

              <div
                css={css`
                  align-self: stretch;
                  height: 32px;
                  justify-content: flex-end;
                  align-items: center;
                  gap: 8px;
                  display: inline-flex;
                  margin-top: 8px;
                `}
              >
                <button
                  css={css`
                    border: 1px solid ${colors.NEUTRAL_0};
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: transparent;
                    cursor: pointer;
                    &:hover {
                      opacity: 0.9;
                    }
                  `}
                  onClick={handleCancel}
                >
                  <CancelIcon size={24} color={colors.NEUTRAL_0} />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    Cancel
                  </Text>
                </button>
                <button
                  css={css`
                    border: 1px solid
                      ${isContent
                        ? editTitle.trim() && !isRichTextEmpty(editContent)
                          ? colors.PRIMARY
                          : colors.NEUTRAL_30
                        : editTitle.trim()
                        ? colors.PRIMARY
                        : colors.NEUTRAL_30};
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: transparent;
                    cursor: ${isContent
                      ? editTitle.trim() && !isRichTextEmpty(editContent)
                        ? 'pointer'
                        : 'not-allowed'
                      : editTitle.trim()
                      ? 'pointer'
                      : 'not-allowed'};
                    opacity: ${isContent
                      ? editTitle.trim() && !isRichTextEmpty(editContent)
                        ? 1
                        : 0.5
                      : editTitle.trim()
                      ? 1
                      : 0.5};
                    &:hover {
                      opacity: ${isContent
                        ? editTitle.trim() && !isRichTextEmpty(editContent)
                          ? 0.9
                          : 0.5
                        : editTitle.trim()
                        ? 0.9
                        : 0.5};
                    }
                  `}
                  onClick={handleSave}
                  disabled={
                    isContent
                      ? !editTitle.trim() || isRichTextEmpty(editContent)
                      : !editTitle.trim()
                  }
                >
                  <SaveIcon
                    size={24}
                    color={
                      isContent
                        ? editTitle.trim() && !isRichTextEmpty(editContent)
                          ? colors.PRIMARY
                          : colors.NEUTRAL_30
                        : editTitle.trim()
                        ? colors.PRIMARY
                        : colors.NEUTRAL_30
                    }
                  />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={
                      isContent
                        ? editTitle.trim() && !isRichTextEmpty(editContent)
                          ? colors.PRIMARY
                          : colors.NEUTRAL_30
                        : editTitle.trim()
                        ? colors.PRIMARY
                        : colors.NEUTRAL_30
                    }
                  >
                    Save
                  </Text>
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        id={id}
        ref={setNodeRef}
        style={style}
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 8px;
          align-self: stretch;
          width: 100%;
          padding: 8px;
          padding-top: 8px;
          background: ${isReserved
            ? colors.SECONDARY_500
            : colors.SECONDARY_400};
          border-radius: 16px;
          border: 1px solid ${colors.SECONDARY_200};
          opacity: ${isEnabled ? 1 : 0.8}!important;

          &:hover .section-drag-handle {
            opacity: 1;
          }
        `}
      >
        {/* Section Header - Arrow, Title, Drag Handle, More Menu on Same Row.
            When disabled, only the title/chevron dim; the status + action menu
            stay full-opacity so the user feels they can re-enable it. */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            position: relative;
          `}
        >
          {/* Collapse/Expand Toggle */}
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              align-self: center;
              width: 24px;
              height: 24px;
              cursor: pointer;
              border-radius: 4px;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ChevronIcon
              isOpen={!isCollapsed}
              size={20}
              color={colors.NEUTRAL_100}
            />
          </div>

          {/* Title and Subtitle */}
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              min-height: 46px;
              justify-content: center;
              opacity: ${isEnabled ? 1 : 0.5};
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='semiBold'
              color={colors.NEUTRAL_0}
              style='text-transform: capitalize; font-size: 20px;'
            >
              {title}{' '}
              {isReserved && (
                <span
                  css={css`
                    font-size: 12px;
                    color: ${colors.NEUTRAL_50};
                  `}
                >
                  (Reserved)
                </span>
              )}
            </Text>
            {subtitle && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {subtitle}
              </Text>
            )}
          </div>

          {/* Drag Handle - Centered and Top Aligned, visible on hover */}
          <div
            {...attributes}
            {...listeners}
            className='section-drag-handle'
            css={css`
              position: absolute;
              left: 50%;
              top: -6px;
              transform: translateX(-50%);
              display: flex;
              align-items: center;
              justify-content: center;
              width: 24px;
              height: 24px;
              cursor: grab;
              border-radius: 4px;
              opacity: ${isDragging ? 1 : 0};
              transition: opacity 0.15s ease-in-out;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
              &:active {
                cursor: grabbing;
              }
            `}
          >
            <DragHandle size={16} color={colors.NEUTRAL_50} />
          </div>

          {/* Action Buttons */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding-right: 9px;
            `}
          >
            {/* Read-only visibility status (toggle lives in the action menu) */}
            {!isReserved && (
              <span
                css={css`
                  display: inline-flex;
                  align-items: center;
                  gap: 6px;
                  color: ${isEnabled ? colors.PRIMARY : colors.NEUTRAL_50};
                  font-size: 12px;
                  font-weight: 500;
                  line-height: 16px;
                  white-space: nowrap;
                `}
              >
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            )}

            {/* More Menu */}
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement='bottomRight'
              overlayStyle={dropdownOverlayStyle}
              overlayClassName='section-dropdown'
              dropdownRender={(menu) =>
                dropdownRender(menu as React.ReactElement)
              }
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  align-self: center;
                  width: 24px;
                  height: 24px;
                  cursor: pointer;
                  border-radius: 4px;
                  &:hover {
                    background: rgba(255, 255, 255, 0.1);
                  }
                  ${isReserved && 'opacity: 0;'}
                  ${isReserved && 'pointer-events: none;'}
                `}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreIcon size={24} color={colors.PRIMARY} />
              </div>
            </Dropdown>
          </div>
        </div>

        {/* Questions List */}
        {sortedLocalQuestions.length > 0 && !isCollapsed && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleQuestionDragEnd}
            >
              <SortableContext
                items={sortedLocalQuestions.map(
                  (question) => question.field_id
                )}
                strategy={verticalListSortingStrategy}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                  `}
                >
                  {sortedLocalQuestions.map((question) => (
                    <DraggableQuestion
                      key={question.field_id}
                      question={question}
                      onToggle={handleQuestionToggle}
                      onDelete={handleQuestionDelete}
                      onUpdate={handleQuestionUpdate}
                      onEditStateChange={(lIsEditing) =>
                        handleQuestionEditStateChange(
                          question.field_id,
                          lIsEditing
                        )
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      isReserved={question.isReserved}
                      isSectionEnabled={isEnabled}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Content Display for Content Sections */}
        {isContent && content && !isCollapsed && (
          <div
            css={css`
              padding: 8px;
              background: ${colors.SECONDARY_300};
              border-radius: 8px;
              margin-top: 8px;
            `}
          >
            <RichTextRenderer content={content} showReadMore={false} />
          </div>
        )}

        {/* "+ Add" dropdown - full width at the bottom of the section.
            Regular sections: Add Question / Add Section / Add Content.
            Content sections: Add Section / Add Content only. */}
        {!isCollapsed && !isAddingQuestion && (
          <Dropdown
            menu={{
              items: [
                ...(!isContent
                  ? [
                      {
                        key: 'add-question',
                        label: (
                          <div
                            css={css`
                              display: flex;
                              justify-content: center;
                              padding: 6px 0;
                            `}
                          >
                            <span
                              css={css`
                                display: inline-flex;
                                align-items: center;
                                gap: 8px;
                                width: 150px;
                              `}
                            >
                              <QuestionCircleOutlined
                                css={css`
                                  color: ${colors.PRIMARY};
                                  font-size: 16px;
                                `}
                              />
                              <Text
                                variant='paragraphMedium'
                                weight='regular'
                                color={colors.NEUTRAL_0}
                              >
                                Add Question
                              </Text>
                            </span>
                          </div>
                        ),
                        onClick: handleNewQuestionClick,
                      },
                    ]
                  : []),
                {
                  key: 'add-section',
                  label: (
                    <div
                      css={css`
                        display: flex;
                        justify-content: center;
                        padding: 6px 0;
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          gap: 8px;
                          width: 150px;
                        `}
                      >
                        <AppstoreAddOutlined
                          css={css`
                            color: ${colors.PRIMARY};
                            font-size: 16px;
                          `}
                        />
                        <Text
                          variant='paragraphMedium'
                          weight='regular'
                          color={colors.NEUTRAL_0}
                        >
                          Add Section
                        </Text>
                      </span>
                    </div>
                  ),
                  onClick: () => onInsertSection?.(id),
                },
                {
                  key: 'add-content',
                  label: (
                    <div
                      css={css`
                        display: flex;
                        justify-content: center;
                        padding: 6px 0;
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          gap: 8px;
                          width: 150px;
                        `}
                      >
                        <FileTextOutlined
                          css={css`
                            color: ${colors.PRIMARY};
                            font-size: 16px;
                          `}
                        />
                        <Text
                          variant='paragraphMedium'
                          weight='regular'
                          color={colors.NEUTRAL_0}
                        >
                          Add Content
                        </Text>
                      </span>
                    </div>
                  ),
                  onClick: () => onInsertContent?.(id),
                },
              ],
            }}
            trigger={['click']}
            placement='bottomLeft'
            overlayStyle={dropdownOverlayStyle}
            dropdownRender={(menu) =>
              dropdownRender(menu as React.ReactElement)
            }
            disabled={!isEnabled}
          >
            <div
              css={css`
                margin-top: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 10px;
                border: 1px solid
                  ${isEnabled ? colors.PRIMARY : colors.NEUTRAL_50};
                border-radius: 8px;
                cursor: ${isEnabled ? 'pointer' : 'default'};
                opacity: ${isEnabled ? 1 : 0.5};
                &:hover {
                  opacity: ${isEnabled ? 0.8 : 0.5};
                  background: ${isEnabled
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'none'};
                }
              `}
            >
              <PlusCircleOutlined
                css={css`
                  color: ${isEnabled ? colors.PRIMARY : colors.NEUTRAL_50};
                  font-size: 16px;
                `}
              />
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={isEnabled ? colors.PRIMARY : colors.NEUTRAL_50}
              >
                Add
              </Text>
              <DownOutlined
                css={css`
                  color: ${isEnabled ? colors.PRIMARY : colors.NEUTRAL_50};
                  font-size: 12px;
                  margin-left: 4px;
                `}
              />
            </div>
          </Dropdown>
        )}

        {/* Add Question Form */}
        {isAddingQuestion && !isCollapsed && (
          <div
            ref={addQuestionFormRef}
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              margin-top: 8px;
              padding: 8px;
              background: ${colors.SECONDARY_300};
              border-radius: 8px;
              border: 1px solid ${colors.PRIMARY};
            `}
          >
            {/* Type and Label Row */}
            <div
              css={css`
                display: flex;
                gap: 16px;
                align-items: flex-start;
              `}
            >
              <div
                css={css`
                  flex: 1 1 0;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: inline-flex;
                `}
              >
                <QuestionTypeSelector
                  label='Question Type *'
                  disabled={false}
                  name='question-type'
                  value={questionForm.type}
                  onChange={handleQuestionTypeChange}
                  fieldErrors={fieldErrors}
                  customError={customError}
                  placeholder='Select Question Type'
                />
              </div>
              <div
                css={css`
                  flex: 1 1 0;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: inline-flex;
                `}
              >
                <TextInput
                  name='question-label'
                  value={questionForm.label}
                  setValue={(value) =>
                    setQuestionForm({ ...questionForm, label: value })
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Question Title *'
                  placeholder='Enter Question Title'
                  disabled={questionForm.type === 'hockey_usa'}
                />
              </div>
            </div>

            {/* Conditional fields based on question type */}
            {isChoiceQuestion({ type: questionForm.type } as Question) && (
              <OptionEditor
                options={questionForm.options}
                onChange={(options) =>
                  setQuestionForm({ ...questionForm, options })
                }
              />
            )}

            {questionForm.type === 'number' && (
              <NumberRangeInputs
                min={questionForm.min}
                max={questionForm.max}
                onMinChange={(min) => setQuestionForm({ ...questionForm, min })}
                onMaxChange={(max) => setQuestionForm({ ...questionForm, max })}
                fieldErrors={fieldErrors}
                customError={customError}
              />
            )}

            {/* Description and Max Character fields side by side for text and textarea */}
            {['text', 'textarea', 'checkbox'].includes(questionForm.type) && (
              <div
                css={css`
                  align-self: stretch;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 16px;
                  display: inline-flex;
                `}
              >
                <div
                  css={css`
                    flex: 1 1 0;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 8px;
                    display: inline-flex;
                  `}
                >
                  <TextInput
                    name='question-description'
                    value={questionForm.description}
                    setValue={(value) =>
                      setQuestionForm({ ...questionForm, description: value })
                    }
                    fieldErrors={fieldErrors}
                    customError={customError}
                    label='Description (optional)'
                    placeholder='Enter question description'
                  />
                </div>
                {/* Only show max character field for text and textarea, not checkbox */}
                {['text', 'textarea'].includes(questionForm.type) && (
                  <div
                    css={css`
                      flex: 1 1 0;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <TextInput
                      name='question-max-char'
                      value={questionForm.max_char}
                      setValue={(value) =>
                        setQuestionForm({ ...questionForm, max_char: value })
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Maximum Characters (optional)'
                      placeholder='Enter Maximum Character Limit'
                      type='number'
                    />
                  </div>
                )}
              </div>
            )}

            {/* Max Character Limit for other text-like fields (email, url) */}
            {['email', 'url'].includes(questionForm.type) && (
              <div
                css={css`
                  flex: 1 1 0;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: inline-flex;
                `}
              >
                <TextInput
                  name='question-max-char'
                  value={questionForm.max_char}
                  setValue={(value) =>
                    setQuestionForm({ ...questionForm, max_char: value })
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Maximum Characters (optional)'
                  placeholder='Enter Maximum Character Limit'
                  type='number'
                />
              </div>
            )}

            {/* Description only for country field */}
            {questionForm.type === 'country' && (
              <div
                css={css`
                  flex: 1 1 0;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: inline-flex;
                  width: 100%;
                `}
              >
                <TextInput
                  name='question-description'
                  value={questionForm.description}
                  setValue={(value) =>
                    setQuestionForm({ ...questionForm, description: value })
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Description (Optional)'
                  placeholder='Enter Question Description'
                />
              </div>
            )}

            {/* Validation Type dropdown for hockey_usa */}
            {questionForm.type === 'hockey_usa' && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  width: 50%;
                  margin-top: 8px;
                `}
              >
                <SelectInput
                  label='Validation Type'
                  disabled={false}
                  options={usaHockeyValidationOptions}
                  name='usa_hockey_validation_type'
                  value={questionForm.usa_hockey_validation_type}
                  onChange={(value) =>
                    setQuestionForm({
                      ...questionForm,
                      usa_hockey_validation_type: value as
                        | 'none'
                        | 'full'
                        | 'partial',
                    })
                  }
                  fieldErrors={{}}
                  customError={{}}
                  showSearch={false}
                  allowClear={false}
                />
              </div>
            )}

            {/* Action Buttons Row with Required Switch */}
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
                margin-top: 8px;
              `}
            >
              {/* Switches for hockey_usa */}
              {questionForm.type === 'hockey_usa' && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.WHITE}
                  >
                    Validate Date Segment (Ex. 2023-2024 Season)
                  </Text>
                  <SwitchInput
                    value={questionForm.validate_schedule_dates}
                    onChange={(checked) =>
                      setQuestionForm({
                        ...questionForm,
                        validate_schedule_dates: checked,
                      })
                    }
                    title=''
                    subtitle=''
                  />
                </div>
              )}
              {/* Required Switch on the left */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                >
                  Required
                </Text>
                <SwitchInput
                  value={questionForm.mandatory}
                  onChange={
                    questionForm.type === 'hockey_usa'
                      ? () => {} // No-op function for USA Hockey
                      : (value) =>
                          setQuestionForm({
                            ...questionForm,
                            mandatory: value,
                          })
                  }
                  disabled={questionForm.type === 'hockey_usa'}
                  title=''
                  subtitle=''
                />
              </div>

              {/* Action Buttons on the right */}
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                `}
              >
                <button
                  css={css`
                    border: 1px solid ${colors.NEUTRAL_0};
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: transparent;
                    cursor: pointer;
                    &:hover {
                      opacity: 0.9;
                    }
                  `}
                  onClick={handleQuestionCancel}
                >
                  <CancelIcon size={24} color={colors.NEUTRAL_0} />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    Cancel
                  </Text>
                </button>
                <button
                  disabled={!isAddFormValid}
                  css={css`
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: ${colors.PRIMARY};
                    border: none;
                    ${getDisabledButtonStyles(!isAddFormValid)}
                    &:hover:not(:disabled) {
                      opacity: 0.9;
                    }
                  `}
                  onClick={handleQuestionSave}
                >
                  <SaveIcon size={24} color={colors.SECONDARY_300} />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.SECONDARY_300}
                  >
                    Save
                  </Text>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
)

DraggableSection.displayName = 'DraggableSection'

export default DraggableSection
